/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ILinksData } from '../ext-tile-list-item.props.autogenerated';

/**
 * Tile List links.
 */
export interface ITileListLinks {
    links: ILinksData[];
    requestContext: Msdyn365.IRequestContext;
    telemetryContent: ITelemetryContent;
    onTextChange?(index: number): (event: Msdyn365.ContentEditableEvent) => void;
}

/**
 *
 * ITileListLinks component.
 * @extends {React.PureComponent<ITileListLinks>}
 */
export class TileListLinks extends React.PureComponent<ITileListLinks> {
    public render(): JSX.Element | null {
        if (!ArrayExtensions.hasElements(this.props.links)) {
            return null;
        }

        // Default module behaviour when in the CMS
        if (this.props.requestContext.params.isEditor) {
            const editableLinks = this._mapEditableLinks(this.props.links);
            return (
                <div className='ms-tile-list__links'>
                    {editableLinks && ArrayExtensions.hasElements(editableLinks) ? (
                        <Msdyn365.Links
                            links={editableLinks}
                            editProps={{ onTextChange: this.props.onTextChange, requestContext: this.props.requestContext }}
                        />
                    ) : null}
                </div>
            );
        }

        return <div className='ms-tile-list__links'>{this.props.links.map(this._renderLink)}</div>;
    }

    private readonly _renderLink = (link: ILinksData, index: number): JSX.Element => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent, '', '');
        const linkText = link.linkText ? link.linkText : '';
        payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.props.telemetryContent, payLoad);

        // Link doesn't actually navigate, the parent anchor handles navigation
        return (
            <div
                key={index}
                className='ms-tile-list__link'
                title={linkText}
                {...attributes}
                onClick={onTelemetryClick(this.props.telemetryContent, payLoad, linkText)}
            >
                {linkText}
            </div>
        );
    };

    /**
     * ITileListLinks component.
     * @param linkdata - Link data.
     * @returns Editable links.
     */
    private readonly _mapEditableLinks = (linkdata: ILinksData[]): Msdyn365.ILinksData[] | null => {
        if (!ArrayExtensions.hasElements(linkdata)) {
            return null;
        }
        const editableLinks: Msdyn365.ILinksData[] = [];
        for (const link of linkdata) {
            // Construct telemetry attribute to render
            const payLoad = getPayloadObject('click', this.props.telemetryContent, '', '');
            const linkText = link.linkText ? link.linkText : '';
            payLoad.contentAction.etext = linkText;
            const attributes = getTelemetryAttributes(this.props.telemetryContent, payLoad);
            const editableLink: Msdyn365.ILinksData = {
                ariaLabel: link.ariaLabel,
                className: 'ms-tile-list__link',
                linkText: link.linkText,
                linkUrl: link.linkUrl.destinationUrl,
                openInNewTab: link.openInNewTab,
                role: 'button',
                additionalProperties: attributes,
                onClick: onTelemetryClick(this.props.telemetryContent, payLoad, linkText)
            };
            editableLinks.push(editableLink);
        }

        return editableLinks;
    };
}
export default TileListLinks;
